import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2, FileText, Loader2, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface JourneySignatureStepProps {
  stepId: string;
  userId: string;
  documentType: string;
  documentReady: boolean;
  placeholderMessage?: string;
  isCompleted: boolean;
  onComplete: () => void;
}

export function JourneySignatureStep({
  stepId,
  userId,
  documentType,
  documentReady,
  placeholderMessage,
  isCompleted,
  onComplete,
}: JourneySignatureStepProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Dev-only skip function that bypasses document check
  const handleSkipForDev = async () => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('user_journey_step_progress')
        .upsert({
          user_id: userId,
          step_id: stepId,
          status: 'completed',
          completed_at: new Date().toISOString(),
          verified_completion: true,
          action_data: { signed: true, document_type: documentType, skipped_for_dev: true },
        }, { onConflict: 'user_id,step_id' });

      if (error) throw error;

      // Unlock next step
      const { data: currentStep } = await supabase
        .from('journey_steps')
        .select('step_number')
        .eq('id', stepId)
        .single();

      if (currentStep) {
        const { data: nextStep } = await supabase
          .from('journey_steps')
          .select('id')
          .eq('step_number', currentStep.step_number + 1)
          .eq('is_active', true)
          .single();

        if (nextStep) {
          await supabase
            .from('user_journey_step_progress')
            .upsert({
              user_id: userId,
              step_id: nextStep.id,
              status: 'active',
              started_at: new Date().toISOString(),
            }, { onConflict: 'user_id,step_id' });
        }
      }

      toast({
        title: "Step skipped",
        description: "Moved to next step (dev mode)",
      });
      onComplete();
    } catch (error) {
      console.error('Error skipping step:', error);
      toast({
        title: "Error",
        description: "Failed to skip step. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // For actual document signing
  const handleSign = async () => {
    if (!documentReady) {
      toast({
        title: "Document not available",
        description: "This document is not yet available for signing.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('user_journey_step_progress')
        .upsert({
          user_id: userId,
          step_id: stepId,
          status: 'completed',
          completed_at: new Date().toISOString(),
          verified_completion: true,
          action_data: { signed: true, document_type: documentType },
        }, { onConflict: 'user_id,step_id' });

      if (error) throw error;

      // Unlock next step
      const { data: currentStep } = await supabase
        .from('journey_steps')
        .select('step_number')
        .eq('id', stepId)
        .single();

      if (currentStep) {
        const { data: nextStep } = await supabase
          .from('journey_steps')
          .select('id')
          .eq('step_number', currentStep.step_number + 1)
          .eq('is_active', true)
          .single();

        if (nextStep) {
          await supabase
            .from('user_journey_step_progress')
            .upsert({
              user_id: userId,
              step_id: nextStep.id,
              status: 'active',
              started_at: new Date().toISOString(),
            }, { onConflict: 'user_id,step_id' });
        }
      }

      onComplete();
    } catch (error) {
      console.error('Error completing step:', error);
      toast({
        title: "Error",
        description: "Failed to save progress. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isCompleted) {
    return (
      <div className="flex items-center justify-center py-8 text-green-600">
        <CheckCircle2 className="w-6 h-6 mr-2" />
        <span className="font-medium">Document signed</span>
      </div>
    );
  }

  // Document not ready - show placeholder
  if (!documentReady) {
    return (
      <div className="space-y-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {placeholderMessage || "This document is not yet available. Please check back later."}
          </AlertDescription>
        </Alert>

        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <FileText className="w-12 h-12 opacity-30" />
        </div>

        {/* Temporary skip button for development */}
        <div className="flex justify-end">
          <Button
            onClick={handleSkipForDev}
            variant="outline"
            size="sm"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Skipping...
              </>
            ) : (
              "Skip for now (dev only)"
            )}
          </Button>
        </div>
      </div>
    );
  }

  // Document ready - show signature interface
  return (
    <div className="space-y-6">
      <div className="p-6 bg-muted/30 rounded-lg text-center">
        <FileText className="w-16 h-16 mx-auto text-primary mb-4" />
        <p className="text-lg font-medium mb-2">Ready to Sign</p>
        <p className="text-muted-foreground">
          Review and sign the {documentType.replace(/_/g, ' ')} document.
        </p>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={handleSign}
          disabled={isSubmitting}
          size="lg"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Signing...
            </>
          ) : (
            "Review & Sign Document"
          )}
        </Button>
      </div>
    </div>
  );
}
